import React, { useState, useEffect } from "react";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CameraIcon,
} from "@heroicons/react/24/outline";
import { useNavigate, useLocation } from "react-router-dom";
import employeeSidebarMenu from "./employeeSidebarMenu";

const SIDEBAR_WIDTH = 260;
const COLLAPSED_WIDTH = 72;

const EmployeeSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [collapsed, setCollapsed] = useState(false);
  const [profileImage, setProfileImage] = useState(
    localStorage.getItem("userProfileImage") ||
      "https://images.unsplash.com/photo-1560250097-0b93528c311a"
  );

  const userName = localStorage.getItem("userName") || "Employee";

  useEffect(() => {
    const syncImage = (e) => {
      if (!e || e.key === "userProfileImage") {
        const stored = localStorage.getItem("userProfileImage");
        if (stored) setProfileImage(stored);
      }
    };
    window.addEventListener("storage", syncImage);
    return () => window.removeEventListener("storage", syncImage);
  }, []);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageURL = URL.createObjectURL(file);
      setProfileImage(imageURL);
      localStorage.setItem("userProfileImage", imageURL);
    }
  };

  return (
    <>
      {/* REFINED COLLAPSE HANDLE - REDUCED PADDING/SIZE */}
      <div
        onClick={() => setCollapsed(!collapsed)}
        className="fixed top-1/2 -translate-y-1/2 z-[1300] cursor-pointer"
        style={{
          left: collapsed ? COLLAPSED_WIDTH - 10 : SIDEBAR_WIDTH - 10,
          transition: "left 0.3s ease",
        }}
      >
        <div className="w-5 h-10 rounded-full bg-white shadow-xl border border-slate-200 flex items-center justify-center hover:bg-blue-50 transition-colors group">
          {collapsed ? (
            <ChevronRightIcon className="w-3.5 h-3.5 text-blue-700 font-bold" />
          ) : (
            <ChevronLeftIcon className="w-3.5 h-3.5 text-blue-700 font-bold" />
          )}
        </div>
      </div>

      <Drawer
        variant="permanent"
        sx={{
          width: collapsed ? COLLAPSED_WIDTH : SIDEBAR_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: collapsed ? COLLAPSED_WIDTH : SIDEBAR_WIDTH,
            transition: "width 0.3s ease",
            boxSizing: "border-box",
            borderRight: "none",
            height: "100vh",
            overflow: "hidden",
            background: "linear-gradient(180deg, #1E3A8A 0%, #2563EB 100%)",
            color: "#fff",
            display: "flex",
            flexDirection: "column",
            fontFamily: "'Poppins','Roboto',sans-serif",
          },
        }}
      >
        {/* PROFILE */}
        <div className="flex flex-col items-center py-4 border-b border-white/10">
          <div className="relative">
            <img
              src={profileImage}
              alt="profile"
              className="w-14 h-14 rounded-full object-cover border-2 border-white shadow"
            />
            <label className="absolute -bottom-1 -right-1 bg-white text-blue-600 p-1 rounded-full cursor-pointer shadow">
              <CameraIcon className="w-3.5 h-3.5" />
              <input type="file" hidden onChange={handleImageUpload} />
            </label>
          </div>

          {!collapsed && (
            <p className="mt-2 text-sm font-semibold truncate">
              {userName}
            </p>
          )}
        </div>

        {/* MENU */}
        <List disablePadding sx={{ px: 1, pt: 1, flexGrow: 1 }}>
          {employeeSidebarMenu
            .flatMap((g) => g.items)
            .filter((item) => item.label !== "My Learnings")
            .map((item) => {
              const Icon = item.icon;
              const active = location.pathname === item.path;

              return (
                <ListItemButton
                  key={item.label}
                  onClick={() => navigate(item.path)}
                  sx={{
                    my: 0.25,
                    py: 0.7,
                    px: collapsed ? 1 : 1.5,
                    borderRadius: "8px",
                    justifyContent: collapsed ? "center" : "flex-start",
                    backgroundColor: active
                      ? "rgba(255,255,255,0.18)"
                      : "transparent",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.12)",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 32,
                      color: "#fff",
                      mr: collapsed ? 0 : 1,
                    }}
                  >
                    <Icon className="w-5 h-5" />
                  </ListItemIcon>

                  {!collapsed && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: 12.5,
                        fontWeight: active ? 600 : 500,
                        fontFamily: "'Poppins','Roboto',sans-serif",
                      }}
                    />
                  )}
                </ListItemButton>
              );
            })}
        </List>
      </Drawer>
    </>
  );
};

export default EmployeeSidebar;